/**
 * Thin wrapper around the AssemblyAI v3 streaming WebSocket. Zero React. Knows
 * nothing about audio capture (it just accepts PCM16 chunks) or about the
 * `SpeechProvider` interface — `assemblyAIProvider.ts` is the only caller.
 */
import { STT_CONFIG } from '@/config/appConfig';

export interface AssemblyBeginMessage {
  type: 'Begin';
  id?: string;
  expires_at?: number;
}

export interface AssemblyTurnMessage {
  type: 'Turn';
  turn_order: number;
  transcript: string;
  end_of_turn: boolean;
  turn_is_formatted?: boolean;
}

export interface AssemblyTerminationMessage {
  type: 'Termination';
}

export type AssemblyServerMessage = AssemblyBeginMessage | AssemblyTurnMessage | AssemblyTerminationMessage;

export interface AssemblyStreamCallbacks {
  onBegin: () => void;
  onTurn: (turn: AssemblyTurnMessage) => void;
  onTermination: () => void;
  onError: (error: unknown) => void;
  onClose: (event: CloseEvent) => void;
}

export interface AssemblyStreamHandle {
  send(pcm: ArrayBuffer): void;
  /** Asks the server to flush and end the session gracefully; the final Turn(s)
   *  and the Termination message arrive asynchronously after this. */
  terminate(): void;
  /** Hard local teardown: detaches handlers, then closes. Idempotent. */
  close(): void;
}

const MAX_BUFFERED_BYTES = STT_CONFIG.maxBufferedBytes;

export function connectAssemblyStream(token: string, callbacks: AssemblyStreamCallbacks): AssemblyStreamHandle {
  const params = new URLSearchParams({
    token,
    sample_rate: '16000',
    encoding: 'pcm_s16le',
    speech_model: 'universal-3-6-pro',
    format_turns: 'true',
    min_turn_silence: '560',
    max_turn_silence: '2400',
    end_of_turn_confidence_threshold: '0.7',
    inactivity_timeout: '30',
    keyterms_prompt: JSON.stringify(STT_CONFIG.keytermsPrompt),
  });

  const ws = new WebSocket(`wss://streaming.assemblyai.com/v3/ws?${params.toString()}`);

  ws.onmessage = (event: MessageEvent<string>) => {
    let message: AssemblyServerMessage;
    try {
      message = JSON.parse(event.data);
    } catch {
      return;
    }
    if (message.type === 'Begin') {
      callbacks.onBegin();
    } else if (message.type === 'Turn') {
      callbacks.onTurn(message);
    } else if (message.type === 'Termination') {
      callbacks.onTermination();
    }
  };
  ws.onerror = (event) => callbacks.onError(event);
  ws.onclose = (event) => callbacks.onClose(event);

  return {
    send(pcm: ArrayBuffer): void {
      if (ws.readyState !== WebSocket.OPEN) return;
      if (ws.bufferedAmount > MAX_BUFFERED_BYTES) return;
      ws.send(pcm);
    },
    terminate(): void {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'Terminate' }));
      }
    },
    close(): void {
      // Detach handlers before closing: an onclose firing after the caller has
      // already torn down its own state would otherwise setState on a session
      // that no longer exists.
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    },
  };
}
